import { useEffect, useState } from "react";
import { ChevronUp, ChevronDown, ChevronRight, Save, RotateCcw, Globe, UserCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatDetail } from "@/lib/api";
import {
  NAV_GROUPS, loadNavOrder, readNavOrderCache, resolveNavOrder, defaultNavOrder,
  savePersonalNavOrder, resetPersonalNavOrder, saveGlobalNavOrder, resetGlobalNavOrder,
  notifyNavOrderChanged,
} from "@/lib/navOrder";

const slug = (to) => String(to).replace(/^\/+/, "").replace(/\//g, "-") || "root";

/**
 * NavOrderSettings — atur urutan grup menu sidebar + sub-fitur di dalamnya
 * (2026-06-13 R2, user request). Klik nama grup untuk expand & atur urutan
 * sub-fiturnya (naik/turun).
 * Super admin (BOD Lv1): perubahan jadi DEFAULT GLOBAL untuk semua user.
 * User lain: perubahan hanya berlaku untuk tampilan akunnya sendiri.
 */
export default function NavOrderSettings({ isSuperAdmin }) {
  const [res, setRes] = useState(null);
  const [list, setList] = useState(() => defaultNavOrder());
  const [expanded, setExpanded] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    loadNavOrder().then((r) => { if (alive) { setRes(r); setList(resolveNavOrder(r)); } });
    return () => { alive = false; };
  }, []);

  const toggleExpand = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const moveGroup = (i, dir) => setList((l) => {
    const j = i + dir;
    if (j < 0 || j >= l.groups.length) return l;
    const groups = [...l.groups];
    [groups[i], groups[j]] = [groups[j], groups[i]];
    return { ...l, groups };
  });

  const moveItem = (gk, i, dir) => setList((l) => {
    const arr = [...(l.items[gk] || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return l;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...l, items: { ...l.items, [gk]: arr } };
  });

  const currentSaved = resolveNavOrder(res || {});
  const dirty = JSON.stringify(list) !== JSON.stringify(currentSaved);

  const save = async () => {
    setBusy(true);
    try {
      if (isSuperAdmin) {
        await saveGlobalNavOrder(list);
        toast.success("Urutan default global tersimpan — berlaku untuk semua user.");
      } else {
        await savePersonalNavOrder(list);
        toast.success("Urutan menu Anda tersimpan.");
      }
      notifyNavOrderChanged();
      setRes(readNavOrderCache());
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Gagal menyimpan urutan menu.");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      if (isSuperAdmin) {
        await resetGlobalNavOrder();
        toast.success("Default global dihapus — kembali ke urutan bawaan sistem.");
      } else {
        await resetPersonalNavOrder();
        toast.success("Override personal dihapus — kembali mengikuti default admin.");
      }
      notifyNavOrderChanged();
      const r = await loadNavOrder();
      setRes(r);
      setList(resolveNavOrder(r));
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Gagal mereset urutan menu.");
    } finally { setBusy(false); }
  };

  const meta = Object.fromEntries(NAV_GROUPS.map((g) => [g.key, g]));
  const canReset = isSuperAdmin ? !!res?.global : !!res?.personal;

  const moveBtn = (testid, label, disabled, onClick, children) => (
    <button type="button" data-testid={testid} aria-label={label}
      disabled={disabled || busy} onClick={onClick}
      className="p-1.5 border border-slate-200 text-slate-500 hover:text-[#0B1D3A] hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
      {children}
    </button>
  );

  return (
    <div data-testid="nav-order-settings" className="space-y-4">
      <div className={`border px-3 py-2 text-[11px] flex items-start gap-2 ${isSuperAdmin ? "border-amber-200 bg-amber-50 text-amber-900" : "border-sky-200 bg-sky-50 text-sky-900"}`}
        data-testid="nav-order-info">
        {isSuperAdmin ? <Globe size={14} className="mt-0.5 shrink-0" /> : <UserCircle size={14} className="mt-0.5 shrink-0" />}
        <div>
          {isSuperAdmin ? (
            <>
              <div className="font-semibold">Anda login sebagai Super Admin</div>
              <div>Urutan yang Anda simpan menjadi <b>default global</b> — otomatis dipakai semua user yang tidak punya pengaturan personal.</div>
            </>
          ) : (
            <>
              <div className="font-semibold">Pengaturan personal</div>
              <div>
                Urutan ini hanya berlaku untuk akun Anda, mengesampingkan default dari admin.{" "}
                {res?.personal
                  ? <b>Override personal sedang aktif.</b>
                  : <>Saat ini Anda <b>mengikuti default admin</b> — ubah urutan lalu simpan untuk membuat override personal.</>}
              </div>
            </>
          )}
          <div className="mt-1 opacity-80">Klik nama grup untuk membuka &amp; mengatur urutan <b>sub-fitur</b> di dalamnya.</div>
        </div>
      </div>

      <div className="border border-slate-200 divide-y divide-slate-100" data-testid="nav-order-list">
        {list.groups.map((key, i) => {
          const g = meta[key];
          if (!g) return null;
          const Icon = g.items[0]?.icon;
          const items = list.items[key] || [];
          const itemMeta = Object.fromEntries(g.items.map((n) => [n.to, n]));
          const isOpen = expanded.has(key);
          const reorderable = items.length > 1;
          return (
            <div key={key} data-testid={`nav-order-row-${key}`} className="bg-white">
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className="w-6 text-center text-[10px] font-mono tabular-nums text-slate-400 shrink-0">{i + 1}</span>
                {reorderable ? (
                  <button type="button" data-testid={`nav-order-expand-${key}`} onClick={() => toggleExpand(key)}
                    aria-expanded={isOpen} aria-label={`${isOpen ? "Tutup" : "Buka"} sub-fitur ${g.label}`}
                    className="p-1 text-slate-400 hover:text-[#0B1D3A] transition-colors shrink-0">
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                ) : <span className="w-6 shrink-0" />}
                {Icon && <Icon size={15} className="text-[#0B1D3A] shrink-0" />}
                <button type="button" onClick={() => reorderable && toggleExpand(key)}
                  className={`min-w-0 flex-1 text-left ${reorderable ? "cursor-pointer" : "cursor-default"}`}>
                  <div className="text-xs font-semibold text-[#0B1D3A]">{g.label}</div>
                  <div className="text-[10px] text-slate-500 truncate">{g.items.map((n) => n.label).join(" · ")}</div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {moveBtn(`nav-order-up-${key}`, `Naikkan ${g.label}`, i === 0, () => moveGroup(i, -1), <ChevronUp size={13} />)}
                  {moveBtn(`nav-order-down-${key}`, `Turunkan ${g.label}`, i === list.groups.length - 1, () => moveGroup(i, 1), <ChevronDown size={13} />)}
                </div>
              </div>
              {isOpen && reorderable && (
                <div className="border-t border-slate-100 bg-slate-50/60" data-testid={`nav-order-items-${key}`}>
                  {items.map((to, ii) => {
                    const n = itemMeta[to];
                    if (!n) return null;
                    const NIcon = n.icon;
                    return (
                      <div key={to} data-testid={`nav-order-item-${key}-${slug(to)}`}
                        className="flex items-center gap-2 pl-14 pr-3 py-2 border-b border-slate-100 last:border-b-0">
                        <span className="w-5 text-center text-[9px] font-mono tabular-nums text-slate-400 shrink-0">{ii + 1}</span>
                        {NIcon && <NIcon size={13} className="text-slate-500 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-medium text-slate-700">{n.label}</div>
                          <div className="text-[9px] font-mono text-slate-400">{to}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {moveBtn(`nav-order-item-up-${key}-${slug(to)}`, `Naikkan ${n.label}`, ii === 0, () => moveItem(key, ii, -1), <ChevronUp size={12} />)}
                          {moveBtn(`nav-order-item-down-${key}-${slug(to)}`, `Turunkan ${n.label}`, ii === items.length - 1, () => moveItem(key, ii, 1), <ChevronDown size={12} />)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-2">
        {canReset && (
          <Button type="button" variant="outline" onClick={reset} disabled={busy}
            data-testid="nav-order-reset" className="rounded-none uppercase text-xs tracking-wider">
            <RotateCcw size={13} className="mr-1.5" />
            {isSuperAdmin ? "Reset Default Global" : "Ikuti Default Admin"}
          </Button>
        )}
        <Button type="button" onClick={save} disabled={busy || !dirty}
          data-testid="nav-order-save"
          className="rounded-none bg-[#0B1D3A] hover:bg-[#12284d] text-white uppercase text-xs tracking-wider font-semibold">
          <Save size={13} className="mr-1.5" />
          {busy ? "Menyimpan…" : isSuperAdmin ? "Simpan sebagai Default Global" : "Simpan Urutan Saya"}
        </Button>
      </div>
    </div>
  );
}
